import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ExclusiveAvatarButton from '../ExclusiveAvatarButton';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';

import s from './ExclusiveAvatarGreetingsModal.scss';

export interface Props {
  /** Callback when modal is closed */
  onClose?: () => void;
  /** Image URL for the Halloween masks */
  imageUrl?: string;
}

/**
 * Greetings modal for exclusive avatars Halloween feature
 * Shows announcement about exclusive avatars with CTA to shop
 */
const ExclusiveAvatarGreetingsModal: React.FC<Props> = ({
  onClose,
  imageUrl = 'https://st.deviantart.net/eclipse/illustrations/halloween_2026_masks.png',
}) => {
  const { t } = useTranslation();
  const logBiEvent = useBiLogger();

  const handleShopClick = () => {
    logBiEvent(
      BiData<DefaultBiEvent>({
        evid: 2,
        click_info: 'shop_exclusives_for_avatar',
        typeid: BiContextProvidedValue,
        itemid: BiContextProvidedValue,
      })
    );
    const url = t('commentBox.exclusiveAvatars.greetingsModal.shopUrl');
    window.location.assign(url);
  };

  const handleLearnMoreClick = () => {
    const url = t('commentBox.exclusiveAvatars.greetingsModal.learnMoreUrl');
    window.location.assign(url);
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      heading=" "
      headerClassName={s['header']}
      headerPadding="slim"
      className={s['modal']}
      contentClassName={s['modal-content']}
    >
      <div className={s['content']}>
        <div className={s['text-content']}>
          <p className={s['label']}>
            {t('commentBox.exclusiveAvatars.greetingsModal.label')}
          </p>
          <h2 className={s['title']}>
            {t('commentBox.exclusiveAvatars.greetingsModal.title')}
          </h2>
          <p className={s['description']}>
            {t('commentBox.exclusiveAvatars.greetingsModal.description')}
          </p>
        </div>

        <div className={s['image-container']}>
          <img src={imageUrl} alt="Halloween masks" className={s['image']} />
        </div>

        <div className={s['actions']}>
          <ExclusiveAvatarButton
            onClick={handleShopClick}
            className={s['shop-button']}
          >
            {t('commentBox.exclusiveAvatars.greetingsModal.cta')}
          </ExclusiveAvatarButton>
          <TextButton
            size="small"
            variant="main"
            onClick={handleLearnMoreClick}
          >
            {t('common.learnMore')}
          </TextButton>
        </div>
      </div>
    </Modal>
  );
};

ExclusiveAvatarGreetingsModal.displayName = 'ExclusiveAvatarGreetingsModal';

export default ExclusiveAvatarGreetingsModal;
